import React, { useState, useContext, useEffect } from "react";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";

import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Typography,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormLabel,
  Checkbox,
  Divider,
} from "@material-ui/core";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";

const FinalizacaoVendaModal = ({ open, onClose, ticket, onFinalizar }) => {
  const history = useHistory();
  const { user } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const [vendaConcluida, setVendaConcluida] = useState(true);
  const [valorVenda, setValorVenda] = useState("");
  const [motivoNaoVenda, setMotivoNaoVenda] = useState("");
  const [motivoFinalizacao, setMotivoFinalizacao] = useState("");
  const [observacao, setObservacao] = useState("");
  const [motivosFinalizacao, setMotivosFinalizacao] = useState([]);
  const [informarValorVenda, setInformarValorVenda] = useState(false);
  const [observacaoObrigatoria, setObservacaoObrigatoria] = useState(false);
  const [loadingConfig, setLoadingConfig] = useState(true);

  // Custom fields
  const [customFields, setCustomFields] = useState([]);
  const [customValues, setCustomValues] = useState({});

  const handleClose = () => {
    onClose();
    setVendaConcluida(true);
    setValorVenda("");
    setMotivoNaoVenda("");
    setMotivoFinalizacao("");
    setObservacao("");
    setCustomValues({});
  };

  useEffect(() => {
    if (open) {
      setLoadingConfig(true);
      Promise.all([
        fetchMotivosFinalizacao(),
        fetchFinalizationConfig(),
        fetchCustomFields(),
      ]).then(() => setLoadingConfig(false));
    }
  }, [open]);

  const fetchMotivosFinalizacao = async () => {
    try {
      const queueId = ticket?.queue?.id || ticket?.queueId || "";
      const params = queueId ? `?queueId=${queueId}` : "";
      const { data } = await api.get(`/ticketFinalizationReasons${params}`);
      setMotivosFinalizacao(data);
    } catch (err) {
      console.error("Erro ao buscar motivos de finalização:", err);
    }
  };

  const fetchFinalizationConfig = async () => {
    try {
      const queueId = ticket?.queue?.id || ticket?.queueId;
      if (queueId) {
        // Buscar config resolvida (queue override > company default)
        const { data } = await api.get(`/queue/${queueId}/finalizationConfig`);
        setInformarValorVenda(data.informarValorVenda || false);
        setObservacaoObrigatoria(data.observacaoObrigatoria || false);
      } else {
        // Sem fila, usa config da empresa
        const { data } = await api.get("/companySettings");
        setInformarValorVenda(data.informarValorVenda || false);
        setObservacaoObrigatoria(data.observacaoFinalizacaoObrigatoria || false);
      }
    } catch (err) {
      console.error("Erro ao buscar configurações de finalização:", err);
      // Fallback para config da empresa
      try {
        const { data } = await api.get("/companySettings");
        setInformarValorVenda(data.informarValorVenda || false);
        setObservacaoObrigatoria(data.observacaoFinalizacaoObrigatoria || false);
      } catch (e) {
        console.error("Erro ao buscar configurações da empresa:", e);
      }
    }
  };

  const fetchCustomFields = async () => {
    try {
      const queueId = ticket?.queue?.id || ticket?.queueId || "";
      const params = queueId ? `?queueId=${queueId}` : "";
      const { data } = await api.get(`/finalizationCustomFields${params}`);
      setCustomFields(data);
      // Inicializar valores vazios
      const initial = {};
      data.forEach(f => {
        initial[f.name] = f.fieldType === "checkbox" ? false : "";
      });
      setCustomValues(initial);
    } catch (err) {
      console.error("Erro ao buscar campos customizados:", err);
    }
  };

  const updateCustomValue = (fieldName, value) => {
    setCustomValues(prev => ({ ...prev, [fieldName]: value }));
  };

  const handleSubmit = async () => {
    if (informarValorVenda) {
      if (vendaConcluida) {
        if (!valorVenda) {
          toast.error("Por favor, informe o valor da venda.");
          return;
        }
      } else {
        if (!motivoNaoVenda) {
          toast.error("Por favor, selecione o motivo da não venda.");
          return;
        }
      }
    } else {
      if (!motivoFinalizacao) {
        toast.error("Por favor, selecione o motivo da finalização.");
        return;
      }
    }

    // Validar observação obrigatória
    if (observacaoObrigatoria && !observacao.trim()) {
      toast.error("A observação é obrigatória para finalizar este atendimento.");
      return;
    }

    // Validar campos customizados obrigatórios
    for (const field of customFields) {
      if (field.required) {
        const val = customValues[field.name];
        if (val === "" || val === undefined || val === null) {
          toast.error(`O campo "${field.label}" é obrigatório.`);
          return;
        }
      }
    }

    const ticketData = {
      status: "closed",
      userId: user?.id || null,
      queueId: ticket?.queue?.id || ticket?.queueId || null,
    };

    ticketData.observacaoFinalizacao = observacao.trim() || null;

    if (informarValorVenda) {
      ticketData.finalizadoComVenda = vendaConcluida;
      if (vendaConcluida) {
        ticketData.valorVenda = parseFloat(valorVenda);
        ticketData.motivoNaoVenda = null;
        ticketData.motivoFinalizacao = null;
      } else {
        ticketData.valorVenda = null;
        ticketData.motivoNaoVenda = motivoNaoVenda;
        ticketData.motivoFinalizacao = null;
      }
    } else {
      ticketData.finalizadoComVenda = null;
      ticketData.valorVenda = null;
      ticketData.motivoNaoVenda = null;
      ticketData.motivoFinalizacao = motivoFinalizacao;
    }

    // Campos customizados — só enviar se houver
    if (customFields.length > 0) {
      const filledCustom = {};
      Object.entries(customValues).forEach(([key, val]) => {
        if (val !== "" && val !== false && val !== null && val !== undefined) {
          filledCustom[key] = val;
        }
      });
      if (Object.keys(filledCustom).length > 0) {
        ticketData.finalizationCustomData = filledCustom;
      }
    }

    if (onFinalizar) onFinalizar(ticketData);
    handleClose();
  };

  const renderCustomField = (field) => {
    switch (field.fieldType) {
      case "text":
        return (
          <TextField
            key={field.id}
            fullWidth
            label={field.label + (field.required ? " *" : "")}
            value={customValues[field.name] || ""}
            onChange={(e) => updateCustomValue(field.name, e.target.value)}
            variant="outlined"
            margin="dense"
          />
        );
      case "number":
        return (
          <TextField
            key={field.id}
            fullWidth
            label={field.label + (field.required ? " *" : "")}
            type="number"
            value={customValues[field.name] || ""}
            onChange={(e) => updateCustomValue(field.name, e.target.value)}
            variant="outlined"
            margin="dense"
          />
        );
      case "textarea":
        return (
          <TextField
            key={field.id}
            fullWidth
            label={field.label + (field.required ? " *" : "")}
            value={customValues[field.name] || ""}
            onChange={(e) => updateCustomValue(field.name, e.target.value)}
            variant="outlined"
            margin="dense"
            multiline
            rows={2}
          />
        );
      case "select":
        return (
          <FormControl key={field.id} fullWidth variant="outlined" margin="dense">
            <InputLabel>{field.label + (field.required ? " *" : "")}</InputLabel>
            <Select
              value={customValues[field.name] || ""}
              onChange={(e) => updateCustomValue(field.name, e.target.value)}
              label={field.label + (field.required ? " *" : "")}
            >
              <MenuItem value=""><em>Selecione</em></MenuItem>
              {(field.options || []).map((opt, idx) => (
                <MenuItem key={idx} value={opt}>{opt}</MenuItem>
              ))}
            </Select>
          </FormControl>
        );
      case "checkbox":
        return (
          <FormControlLabel
            key={field.id}
            control={
              <Checkbox
                checked={!!customValues[field.name]}
                onChange={(e) => updateCustomValue(field.name, e.target.checked)}
                color="primary"
              />
            }
            label={field.label + (field.required ? " *" : "")}
          />
        );
      default:
        return null;
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {informarValorVenda ? "Finalização de Atendimento — Valor de Venda" : "Finalização de Atendimento"}
      </DialogTitle>
      {loadingConfig ? (
        <DialogContent>
          <Typography>Carregando configurações...</Typography>
        </DialogContent>
      ) : (
        <DialogContent>
          <Grid container spacing={2}>
            {informarValorVenda ? (
              <>
                <Grid item xs={12}>
                  <FormControl component="fieldset">
                    <FormLabel component="legend">
                      A venda foi concluída?
                    </FormLabel>
                    <RadioGroup
                      value={vendaConcluida}
                      onChange={(e) =>
                        setVendaConcluida(e.target.value === "true")
                      }
                    >
                      <FormControlLabel
                        value={true}
                        control={<Radio />}
                        label="Sim, venda concluída"
                      />
                      <FormControlLabel
                        value={false}
                        control={<Radio />}
                        label="Não, venda não concluída"
                      />
                    </RadioGroup>
                  </FormControl>
                </Grid>
                {vendaConcluida ? (
                  <Grid item xs={12}>
                    <TextField
                      label="Valor da Venda (R$)"
                      type="number"
                      value={valorVenda}
                      onChange={(e) => setValorVenda(e.target.value)}
                      fullWidth
                      variant="outlined"
                      margin="dense"
                      inputProps={{
                        step: "0.01",
                        min: "0",
                      }}
                      placeholder="0,00"
                    />
                  </Grid>
                ) : (
                  <Grid item xs={12}>
                    <FormControl
                      fullWidth
                      variant="outlined"
                      margin="dense"
                      required
                    >
                      <InputLabel>Motivo da Não Venda *</InputLabel>
                      <Select
                        value={motivoNaoVenda}
                        onChange={(e) => setMotivoNaoVenda(e.target.value)}
                        label="Motivo da Não Venda *"
                      >
                        <MenuItem value="">
                          <em>Selecione um motivo</em>
                        </MenuItem>
                        {motivosFinalizacao.map((motivo) => (
                          <MenuItem key={motivo.id} value={motivo.name}>
                            {motivo.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                )}
              </>
            ) : (
              <Grid item xs={12}>
                <FormControl
                  fullWidth
                  variant="outlined"
                  margin="dense"
                  required
                >
                  <InputLabel>Motivo da Finalização *</InputLabel>
                  <Select
                    value={motivoFinalizacao}
                    onChange={(e) => setMotivoFinalizacao(e.target.value)}
                    label="Motivo da Finalização *"
                  >
                    <MenuItem value="">
                      <em>Selecione um motivo</em>
                    </MenuItem>
                    {motivosFinalizacao.map((motivo) => (
                      <MenuItem key={motivo.id} value={motivo.name}>
                        {motivo.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}

            {/* Campo de observação (sempre visível) */}
            <Grid item xs={12}>
              <TextField
                label={observacaoObrigatoria ? "Observação *" : "Observação (opcional)"}
                value={observacao}
                onChange={(e) => setObservacao(e.target.value)}
                fullWidth
                variant="outlined"
                margin="dense"
                multiline
                rows={3}
                placeholder="Anotações sobre o atendimento..."
                required={observacaoObrigatoria}
              />
            </Grid>

            {/* Campos customizados */}
            {customFields.length > 0 && (
              <>
                <Grid item xs={12}>
                  <Divider style={{ margin: "8px 0" }} />
                  <Typography variant="subtitle2" color="textSecondary" style={{ marginTop: 8 }}>
                    Informações adicionais
                  </Typography>
                </Grid>
                {customFields.map((field) => (
                  <Grid item xs={12} key={field.id}>
                    {renderCustomField(field)}
                  </Grid>
                ))}
              </>
            )}
          </Grid>
        </DialogContent>
      )}
      <DialogActions>
        <Button onClick={handleClose} color="secondary" disabled={loading}>
          Cancelar
        </Button>
        <Button
          onClick={handleSubmit}
          color="primary"
          variant="contained"
          disabled={loading || loadingConfig}
        >
          {loading ? "Finalizando..." : "Finalizar Atendimento"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FinalizacaoVendaModal;
